package org.teddapp.project.ui.portfolio

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Competence
import com.tedd.db.Competence_criterion
import com.tedd.db.Competence_family
import com.tedd.db.Focus_session
import com.tedd.db.Parcours_step
import com.tedd.db.Portfolio_piece
import com.tedd.db.Portfolio_piece_competence
import com.tedd.db.Project
import com.tedd.db.Skill
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreSnapshot
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.repo.PieceCompetenceRef
import org.teddapp.project.portfolio.CompetenceLevel
import org.teddapp.project.portfolio.PieceImage
import org.teddapp.project.portfolio.PieceLink
import org.teddapp.project.portfolio.PieceStatus
import org.teddapp.project.portfolio.aggregateLevel
import org.teddapp.project.portfolio.export.PortfolioDocumentBuilder
import org.teddapp.project.portfolio.export.PortfolioExporters
import org.teddapp.project.portfolio.export.PortfolioMarkdown
import org.teddapp.project.portfolio.export.SkillDocInfo
import org.teddapp.project.portfolio.levelDistribution
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.ui.TeddTier

enum class PortfolioTab(val label: String) { Recueil("Recueil"), Portfolio("Portfolio") }

enum class PieceFilter(val label: String) { All("Toutes"), Published("Publiées"), Drafts("Brouillons") }

enum class ExportKind(val label: String, val fileName: String) {
    Recueil("Recueil", "recueil-competences"),
    Portfolio("Portfolio", "portfolio"),
    All("Portfolio + recueil", "portfolio-et-recueil"),
}

/** Ce que l'interface sait d'une skill Focus liée ; skill null = la skill a disparu. */
data class SkillLink(val skill: Skill?, val hours: Double, val tier: TeddTier?)

internal sealed interface PortfolioEditor {
    data class Family(val existing: Competence_family?) : PortfolioEditor
    data class Competence(val existing: com.tedd.db.Competence?, val familyId: String?) : PortfolioEditor
    data class Criterion(val existing: Competence_criterion?, val competenceId: String) : PortfolioEditor
    data class Piece(val existing: Portfolio_piece?) : PortfolioEditor
}

internal sealed interface DeleteTarget {
    val name: String
    data class Family(val row: Competence_family) : DeleteTarget { override val name get() = row.name }
    data class Competence(val row: com.tedd.db.Competence) : DeleteTarget { override val name get() = row.name }
    data class Criterion(val row: Competence_criterion) : DeleteTarget { override val name get() = row.label }
    data class Piece(val row: Portfolio_piece) : DeleteTarget { override val name get() = row.title }
}

/**
 * État et gestes du module Portfolio & recueil. Les listes brutes viennent des flux
 * SQLDelight ; tout le reste se dérive à la lecture, sans copie à entretenir.
 */
@Stable
internal class PortfolioModel(
    val repos: TeddRepositories,
    private val scope: CoroutineScope,
) {
    var families by mutableStateOf<List<Competence_family>>(emptyList())
    var competences by mutableStateOf<List<Competence>>(emptyList())
    var criteria by mutableStateOf<List<Competence_criterion>>(emptyList())
    var pieces by mutableStateOf<List<Portfolio_piece>>(emptyList())
    var links by mutableStateOf<List<Portfolio_piece_competence>>(emptyList())
    var skills by mutableStateOf<List<Skill>>(emptyList())
    var projects by mutableStateOf<List<Project>>(emptyList())
    var steps by mutableStateOf<List<Parcours_step>>(emptyList())
    var sessions by mutableStateOf<List<Focus_session>>(emptyList())
    var coreSnapshot by mutableStateOf<CoreSnapshot?>(null)

    var tab by mutableStateOf(PortfolioTab.Recueil)
    var selectedFamilyId by mutableStateOf<String?>(null)
    var selectedCompetenceId by mutableStateOf<String?>(null)
    var selectedPieceId by mutableStateOf<String?>(null)
    var pieceFilter by mutableStateOf(PieceFilter.All)
    var search by mutableStateOf("")

    var editor by mutableStateOf<PortfolioEditor?>(null)
    var confirmDelete by mutableStateOf<DeleteTarget?>(null)
    var exportOpen by mutableStateOf(false)
    var exportDrafts by mutableStateOf(false)
    var exporting by mutableStateOf(false)
    val snackbar = TeddSnackbarState()

    /** Le compte serveur, s'il y en a un : publier sur le site passe par lui. */
    var session: org.teddapp.project.server.ServerSession? = null
    /** La pièce pour laquelle le dialogue « Publier sur le site » est ouvert. */
    var publishing by mutableStateOf<Portfolio_piece?>(null)
    var publishBusy by mutableStateOf(false)
    var publishError by mutableStateOf<String?>(null)

    // ── Dérivés ─────────────────────────────────────────────────────────────

    val familiesById: Map<String, Competence_family> get() = families.associateBy { it.id }
    val competencesById: Map<String, Competence> get() = competences.associateBy { it.id }
    val piecesById: Map<String, Portfolio_piece> get() = pieces.associateBy { it.id }
    val selectedFamily: Competence_family? get() = selectedFamilyId?.let { familiesById[it] }
    val selectedCompetence: Competence? get() = selectedCompetenceId?.let { competencesById[it] }
    val selectedPiece: Portfolio_piece? get() = selectedPieceId?.let { piecesById[it] }

    fun competencesOf(familyId: String?): List<Competence> = competences
        .filter { familyId == null || it.family_id == familyId }
        .filter { matches(it.name, it.description) }

    fun criteriaOf(competenceId: String): List<Competence_criterion> =
        criteria.filter { it.competence_id == competenceId }

    fun levelOf(competenceId: String): CompetenceLevel? = aggregateLevel(criteriaOf(competenceId).map { it.level })
    fun distributionOf(competenceId: String): IntArray = levelDistribution(criteriaOf(competenceId).map { it.level })

    /** Niveau d'une famille : agrégat de tous les critères de ses compétences. */
    fun levelOfFamily(familyId: String): CompetenceLevel? {
        val ids = competences.filter { it.family_id == familyId }.map { it.id }.toSet()
        return aggregateLevel(criteria.filter { it.competence_id in ids }.map { it.level })
    }

    /** Liaisons d'une pièce, dédoublonnées et débarrassées des compétences disparues. */
    fun linksOf(pieceId: String): List<Portfolio_piece_competence> = links
        .filter { it.piece_id == pieceId && it.competence_id in competencesById }
        .distinctBy { Triple(it.piece_id, it.competence_id, it.criterion_id) }
        .sortedBy { it.order_index }

    fun piecesDemonstrating(competenceId: String): List<Portfolio_piece> = links
        .filter { it.competence_id == competenceId }
        .mapNotNull { piecesById[it.piece_id] }
        .distinctBy { it.id }

    fun skillLinkOf(competence: Competence): SkillLink? = competence.skill_id?.let { id ->
        val skill = skills.firstOrNull { it.id == id }
        val core = coreSnapshot?.competences?.get(id)
        val hours = core?.heures ?: (sessions.filter { it.skill_id == id }.sumOf { it.focus_minutes } / 60.0)
        SkillLink(skill, hours, TeddTier.fromCore(core?.palier))
    }

    val visiblePieces: List<Portfolio_piece> get() = pieces
        .filter {
            when (pieceFilter) {
                PieceFilter.All -> true
                PieceFilter.Published -> it.status == PieceStatus.PUBLISHED
                PieceFilter.Drafts -> it.status != PieceStatus.PUBLISHED
            }
        }
        .filter { matches(it.title, it.subtitle, it.body) }

    val publishedCount: Int get() = pieces.count { it.status == PieceStatus.PUBLISHED }

    fun projectName(id: String?): String? = id?.let { pid -> projects.firstOrNull { it.id == pid }?.name }
    fun stepTitle(id: String?): String? = id?.let { sid -> steps.firstOrNull { it.id == sid }?.let { "${it.code} · ${it.title}" } }

    private fun matches(vararg fields: String?): Boolean {
        val q = search.trim().lowercase()
        if (q.isEmpty()) return true
        return fields.any { it?.lowercase()?.contains(q) == true }
    }

    // ── Familles ────────────────────────────────────────────────────────────

    fun saveFamily(existing: Competence_family?, name: String, description: String, color: String, icon: String) {
        if (name.isBlank()) return
        scope.launch {
            if (existing == null) {
                val id = repos.recueil.createFamily(name, description, color, icon)
                selectedFamilyId = id
            } else {
                repos.recueil.updateFamily(existing.id, name, description, color, icon, existing.order_index)
            }
            editor = null
        }
    }

    fun deleteFamily(row: Competence_family) {
        scope.launch {
            repos.recueil.deleteFamily(row.id)
            if (selectedFamilyId == row.id) selectedFamilyId = null
            if (selectedCompetence?.family_id == row.id) selectedCompetenceId = null
            confirmDelete = null
            snackbar.show("Famille supprimée")
        }
    }

    fun reorderFamilies(from: Int, to: Int) {
        val ordered = families.sortedWith(compareBy({ it.order_index }, { it.name })).map { it.id }.toMutableList()
        if (from !in ordered.indices || to !in ordered.indices) return
        ordered.add(to, ordered.removeAt(from))
        scope.launch { repos.recueil.reorderFamilies(ordered) }
    }

    // ── Compétences ─────────────────────────────────────────────────────────

    fun saveCompetence(existing: Competence?, familyId: String, name: String, description: String, skillId: String?) {
        if (name.isBlank() || familyId.isBlank()) return
        scope.launch {
            if (existing == null) {
                val id = repos.recueil.createCompetence(familyId, name, description, skillId)
                selectedCompetenceId = id
            } else {
                repos.recueil.updateCompetence(existing.id, familyId, name, description, skillId, existing.order_index)
            }
            editor = null
        }
    }

    fun deleteCompetence(row: Competence) {
        scope.launch {
            repos.recueil.deleteCompetence(row.id)
            if (selectedCompetenceId == row.id) selectedCompetenceId = null
            confirmDelete = null
            snackbar.show("Compétence supprimée")
        }
    }

    fun unlinkSkill(row: Competence) {
        scope.launch { repos.recueil.unlinkSkill(row.id); snackbar.show("Compétence déliée") }
    }

    fun reorderCompetences(familyId: String, from: Int, to: Int) {
        val ordered = competences.filter { it.family_id == familyId }.map { it.id }.toMutableList()
        if (from !in ordered.indices || to !in ordered.indices) return
        ordered.add(to, ordered.removeAt(from))
        scope.launch { repos.recueil.reorderCompetences(ordered) }
    }

    // ── Critères ────────────────────────────────────────────────────────────

    fun saveCriterion(existing: Competence_criterion?, competenceId: String, label: String, description: String, level: Int) {
        if (label.isBlank()) return
        scope.launch {
            if (existing == null) {
                repos.recueil.createCriterion(competenceId, label, description, level)
            } else {
                repos.recueil.updateCriterion(existing.id, label, description)
                if (existing.level.toInt() != level) repos.recueil.setLevel(existing.id, level, existing.evaluation_note)
            }
            editor = null
        }
    }

    fun setLevel(criterion: Competence_criterion, level: CompetenceLevel) {
        if (criterion.level.toInt() == level.value) return
        scope.launch { repos.recueil.setLevel(criterion.id, level.value, criterion.evaluation_note) }
    }

    fun setLevelNote(criterion: Competence_criterion, note: String) {
        scope.launch { repos.recueil.setLevel(criterion.id, criterion.level.toInt(), note) }
    }

    fun deleteCriterion(row: Competence_criterion) {
        scope.launch { repos.recueil.deleteCriterion(row.id); confirmDelete = null }
    }

    fun reorderCriteria(competenceId: String, from: Int, to: Int) {
        val ordered = criteriaOf(competenceId).map { it.id }.toMutableList()
        if (from !in ordered.indices || to !in ordered.indices) return
        ordered.add(to, ordered.removeAt(from))
        scope.launch { repos.recueil.reorderCriteria(ordered) }
    }

    // ── Pièces ──────────────────────────────────────────────────────────────

    fun savePiece(
        existing: Portfolio_piece?,
        title: String,
        subtitle: String,
        body: String,
        links: List<PieceLink>,
        images: List<PieceImage>,
        dateStart: String,
        dateEnd: String,
        projectId: String?,
        stepId: String?,
        status: String,
        competences: List<PieceCompetenceRef>,
        /** L'identifiant choisi d'avance pour une nouvelle pièce : ses images l'ont déjà. */
        newId: String? = null,
    ) {
        if (title.isBlank()) return
        scope.launch {
            val id = if (existing == null) {
                repos.portfolio.create(
                    title, subtitle, body, links, images, dateStart, dateEnd, projectId, stepId, status, competences,
                    id = newId ?: org.teddapp.project.db.newId("pp"),
                ).also { selectedPieceId = it }
            } else {
                repos.portfolio.update(
                    existing.id, title, subtitle, body, links, images, dateStart, dateEnd, projectId, stepId, status, competences,
                )
                existing.id
            }
            PieceImages.pruneOrphans(repos.db, id, images)
            editor = null
        }
    }

    fun deletePiece(row: Portfolio_piece) {
        scope.launch {
            repos.portfolio.softDelete(row.id)
            if (selectedPieceId == row.id) selectedPieceId = null
            confirmDelete = null
            snackbar.show("Pièce supprimée")
        }
    }

    fun togglePinned(row: Portfolio_piece) {
        scope.launch { repos.portfolio.setPinned(row.id, row.pinned == 0L) }
    }

    /** « Publier » ouvre le dialogue de mise en ligne ; « Retirer » retire du site. */
    fun togglePublished(row: Portfolio_piece) {
        if (row.status == PieceStatus.PUBLISHED) unpublishFromSite(row)
        else { publishError = null; publishing = row }
    }

    /** Le statut local seul, sans toucher au site : pour un appareil qui ne tient pas le site. */
    fun markPublished(row: Portfolio_piece, published: Boolean) {
        scope.launch {
            repos.portfolio.setStatus(row.id, if (published) PieceStatus.PUBLISHED else PieceStatus.DRAFT)
            publishing = null
            snackbar.show(if (published) "Marquée comme publiée" else "Repassée en brouillon")
        }
    }

    private fun serverIdOf(localId: String) =
        if (org.teddapp.project.sync.ServerIds.isUuid(localId)) localId.lowercase() else org.teddapp.project.sync.ServerIds.derive(localId)

    /** Publie la pièce sur le site, dans une langue et sous une adresse ; la copie figée est celle du serveur. */
    fun publishOnSite(row: Portfolio_piece, lang: String, slug: String) {
        val s = session ?: return
        if (publishBusy) return
        publishBusy = true
        publishError = null
        scope.launch {
            try {
                s.authed { api, access -> api.publish(access, "portfolio_piece", serverIdOf(row.id), slug, lang, null) }
                repos.portfolio.setStatus(row.id, PieceStatus.PUBLISHED)
                publishing = null
                snackbar.show("En ligne : /$lang/portfolio/$slug")
            } catch (e: org.teddapp.project.server.ServerException) {
                publishError = org.teddapp.project.ui.admin.publishError(
                    if (e.code == "not_found") org.teddapp.project.ui.admin.NOT_FOUND else org.teddapp.project.ui.settings.serverMessage(e),
                )
            } finally {
                publishBusy = false
            }
        }
    }

    /** Retire du site toutes les versions de la pièce, puis la repasse en brouillon ici. */
    fun unpublishFromSite(row: Portfolio_piece) {
        val s = session
        if (s == null || !s.isSupra) { markPublished(row, false); return }
        scope.launch {
            try {
                val sid = serverIdOf(row.id)
                val online = s.authed { api, access -> api.publications(access) }.filter { it.source_id == sid && it.online }
                online.forEach { p -> s.authed { api, access -> api.unpublish(access, p.id) } }
                repos.portfolio.setStatus(row.id, PieceStatus.DRAFT)
                snackbar.show(if (online.isEmpty()) "Repassée en brouillon" else "Retirée du site")
            } catch (e: org.teddapp.project.server.ServerException) {
                snackbar.show(org.teddapp.project.ui.settings.serverMessage(e))
            }
        }
    }

    fun reorderPieces(from: Int, to: Int) {
        val ordered = pieces.map { it.id }.toMutableList()
        if (from !in ordered.indices || to !in ordered.indices) return
        ordered.add(to, ordered.removeAt(from))
        scope.launch { repos.portfolio.reorder(ordered) }
    }

    /** Ouvre une pièce depuis le recueil : on change d'onglet et on la sélectionne. */
    fun openPiece(id: String) {
        tab = PortfolioTab.Portfolio
        selectedPieceId = id
    }

    fun openCompetence(id: String) {
        tab = PortfolioTab.Recueil
        selectedCompetenceId = id
        selectedFamilyId = competencesById[id]?.family_id
    }

    // ── Export ──────────────────────────────────────────────────────────────

    val pdfAvailable: Boolean get() = PortfolioExporters.savePdf != null
    val markdownToFile: Boolean get() = PortfolioExporters.saveMarkdown != null

    fun buildDocument(kind: ExportKind) = when (kind) {
        ExportKind.Recueil -> recueilDoc()
        ExportKind.Portfolio -> portfolioDoc()
        ExportKind.All -> PortfolioDocumentBuilder.buildAll(recueilDoc(), portfolioDoc(), TODAY_ISO)
    }

    private fun recueilDoc() = PortfolioDocumentBuilder.buildRecueil(
        families, competences, criteria,
        skillInfo = { skillId ->
            val core = coreSnapshot?.competences?.get(skillId)
            val hours = core?.heures ?: sessions.filter { it.skill_id == skillId }.sumOf { it.focus_minutes }.takeIf { it > 0 }?.div(60.0)
            SkillDocInfo(hours, TeddTier.fromCore(core?.palier)?.label)
        },
        generatedAt = TODAY_ISO,
    )

    private fun portfolioDoc() = PortfolioDocumentBuilder.buildPortfolio(
        pieces, links, competences, criteria,
        projectNames = projects.associate { it.id to it.name },
        stepTitles = steps.associate { it.id to "${it.code} · ${it.title}" },
        includeDrafts = exportDrafts,
        generatedAt = TODAY_ISO,
    )

    /**
     * Exporte ; en Markdown sans capacité fichier, [copy] reçoit le texte (presse-papiers).
     */
    fun export(kind: ExportKind, pdf: Boolean, copy: (String) -> Unit) {
        if (exporting) return
        val doc = buildDocument(kind)
        val name = "${kind.fileName}-$TODAY_ISO"
        exportOpen = false
        scope.launch {
            exporting = true
            try {
                if (pdf) {
                    val save = PortfolioExporters.savePdf
                    if (save == null) snackbar.show("Le PDF n'est pas disponible sur cet appareil")
                    else save(name, doc)?.let { snackbar.show("PDF écrit : $it", 4000L) }
                } else {
                    val text = PortfolioMarkdown.render(doc)
                    val save = PortfolioExporters.saveMarkdown
                    if (save == null) { copy(text); snackbar.show("Markdown copié dans le presse-papiers") }
                    else save(name, text)?.let { snackbar.show("Markdown écrit : $it", 4000L) }
                }
            } catch (e: Throwable) {
                snackbar.show("Export impossible : ${e.message ?: e::class.simpleName}", 4000L)
            } finally {
                exporting = false
            }
        }
    }
}

@Composable
internal fun rememberPortfolioModel(repos: TeddRepositories): PortfolioModel {
    val scope = rememberCoroutineScope()
    val model = remember(repos) { PortfolioModel(repos, scope) }
    val core = LocalCoreSession.current
    model.families = repos.recueil.observeFamilies().collectAsState(emptyList()).value
    model.competences = repos.recueil.observeCompetences().collectAsState(emptyList()).value
    model.criteria = repos.recueil.observeCriteria().collectAsState(emptyList()).value
    model.pieces = repos.portfolio.observePieces().collectAsState(emptyList()).value
    model.links = repos.portfolio.observeLinks().collectAsState(emptyList()).value
    model.skills = repos.skills.observeAll().collectAsState(emptyList()).value
    model.projects = repos.projects.observeAll().collectAsState(emptyList()).value
    model.steps = repos.parcours.observeAll().collectAsState(emptyList()).value
    model.sessions = repos.focusSessions.observeAll().collectAsState(emptyList()).value
    model.coreSnapshot = core?.snapshot?.collectAsState()?.value
    return model
}
